// Add Feed Dialog Component
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Rss } from 'lucide-react';
import { api } from '@/lib/api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/Dialog';
import Button from '@/components/Button';
import Input from '@/components/Input';

interface AddFeedDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function AddFeedDialog({ open, onOpenChange }: AddFeedDialogProps) {
  const [url, setUrl] = useState('');
  const [selectedFolderId, setSelectedFolderId] = useState<number | null>(null);
  const [error, setError] = useState('');

  const queryClient = useQueryClient();

  // Get folders for selection
  const { data: foldersData } = useQuery({
    queryKey: ['folders'],
    queryFn: () => api.getFolders(),
  });

  const folders = foldersData?.folders || [];

  // Create feed mutation
  const createFeedMutation = useMutation({
    mutationFn: (data: { url: string; folder_id?: number | null }) => api.createFeed(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feeds'] });
      queryClient.invalidateQueries({ queryKey: ['user-stats'] });
      setUrl('');
      setSelectedFolderId(null);
      setError('');
      onOpenChange(false);
    },
    onError: (err: any) => {
      setError(err.message || 'Failed to add feed');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!url.trim()) {
      setError('Please enter a feed URL');
      return;
    }

    // Basic URL validation
    try {
      new URL(url);
    } catch {
      setError('Please enter a valid URL');
      return;
    }

    createFeedMutation.mutate({
      url: url.trim(),
      folder_id: selectedFolderId,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onClose={() => onOpenChange(false)}>
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Rss className="h-5 w-5 text-primary" />
            <DialogTitle>Add Feed</DialogTitle>
          </div>
          <DialogDescription>
            Enter the URL of an RSS or Atom feed to subscribe
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="space-y-2">
            <label htmlFor="feed-url" className="text-sm font-medium">
              Feed URL
            </label>
            <Input
              id="feed-url"
              type="url"
              placeholder="https://example.com/feed.xml"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              disabled={createFeedMutation.isPending}
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="folder" className="text-sm font-medium">
              Folder (optional)
            </label>
            <select
              id="folder"
              value={selectedFolderId || ''}
              onChange={(e) => setSelectedFolderId(e.target.value ? Number(e.target.value) : null)}
              disabled={createFeedMutation.isPending}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <option value="">No folder</option>
              {folders.map((folder) => (
                <option key={folder.id} value={folder.id}>
                  {folder.name}
                </option>
              ))}
            </select>
          </div>

          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={createFeedMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createFeedMutation.isPending}>
              {createFeedMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                'Add Feed'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
